import apiClient from './apiClient';

// Auth API
export const authAPI = {
  async register(userData) {
    return await apiClient.post('/api/auth/register', userData);
  },

  async login(credentials) {
    // Send JSON data to the API
    return await apiClient.post('/api/auth/login', {
      email: credentials.email,
      password: credentials.password
    }, {
      skipAuth: true // Login doesn't need auth token
    });
  },

  async forgotPassword(data) {
    return await apiClient.post('/api/auth/forgot-password', data, {
      skipAuth: true // Public endpoint doesn't need auth token
    });
  },

  async resetPassword(data) {
    return await apiClient.post('/api/auth/reset-password', data, {
      skipAuth: true // Public endpoint doesn't need auth token
    });
  },

  async changePassword(data) {
    return await apiClient.post('/api/auth/change-password', {
      current_password: data.currentPassword,
      new_password: data.newPassword
    });
  },

  async getUserProfile() {
    return await apiClient.get('/api/auth/profile');
  },

  async updateUserProfile(profileData) {
    return await apiClient.put('/api/auth/profile', profileData);
  },

  async verifyEmail(token) {
    return await apiClient.get(`/api/auth/verify-email/${token}`, {
      skipAuth: true // Public endpoint doesn't need auth token
    });
  },

  async resendVerification() {
    return await apiClient.post('/api/auth/resend-verification');
  },

  async exportAccountData() {
    return await apiClient.get('/api/auth/export');
  },

  async getDeletionRequest() {
    return await apiClient.get('/api/auth/deletion-request');
  },

  async requestAccountDeletion() {
    return await apiClient.post('/api/auth/deletion-request', { confirmation: 'DELETE' });
  }
};

// Endpoints API
export const endpointsAPI = {
  async getUserEndpoints() {
    return await apiClient.get('/api/endpoints/');
  },

  async createEndpoint(endpointData) {
    return await apiClient.post('/api/endpoints/', endpointData);
  },

  async getSpecificEndpoint(endpointId) {
    return await apiClient.get(`/api/endpoints/${endpointId}`);
  },

  async updateEndpoint(endpointId, updateData) {
    return await apiClient.put(`/api/endpoints/${endpointId}`, updateData);
  },

  async deleteEndpoint(endpointId) {
    return await apiClient.delete(`/api/endpoints/${endpointId}`);
  }
};

// Submissions API
export const submissionsAPI = {
  async getAllUserSubmissions(page = 1, size = 100) {
    return await apiClient.get(`/api/submissions/?page=${page}&size=${size}`);
  },

  async getSubmissionsForEndpoint(endpointDbId, page = 1, size = 10) {
    return await apiClient.get(`/api/submissions/${endpointDbId}?page=${page}&size=${size}`);
  },

  async getSubmissionDetails(endpointDbId, submissionId) {
    return await apiClient.get(`/api/submissions/${endpointDbId}/${submissionId}`);
  },

  async getSubmissionById(submissionId) {
    return await apiClient.get(`/api/submissions/details/${submissionId}`);
  },

  async deleteSubmission(endpointDbId, submissionId) {
    return await apiClient.delete(`/api/submissions/${endpointDbId}/${submissionId}`);
  },

  async downloadFile(fileId) {
    return await apiClient.downloadFile(`/api/submissions/files/${fileId}/download`);
  }
};

// Public abuse reporting (no auth)
export const abuseAPI = {
  async submitReport(reportData) {
    return await apiClient.post('/api/abuse/report', reportData, {
      skipAuth: true,
    });
  },
};

// Form submission API (public endpoint)
export const formSubmissionAPI = {
  async submitForm(endpointSlug, formData) {
    return await apiClient.post(`/api/submit/${endpointSlug}`, formData, {
      skipAuth: true // Public endpoint doesn't need auth
    });
  }
};

// Billing API
export const billingAPI = {
  async getUsage() {
    return await apiClient.get('/api/billing/usage');
  },

  async getInvoices() {
    return await apiClient.get('/api/billing/invoices');
  },

  async activateAddOn(addOnType) {
    return await apiClient.post(`/api/billing/add-ons/${addOnType}/activate`);
  },

  async deactivateAddOn(addOnType) {
    return await apiClient.post(`/api/billing/add-ons/${addOnType}/deactivate`);
  },

  async getSubscription() {
    return await apiClient.get('/api/billing/subscription');
  },

  async createEndpointCheckout(blocks = 1) {
    return await apiClient.post('/api/billing/stripe/checkout-endpoints', { blocks });
  },

  async openBillingPortal() {
    return await apiClient.post('/api/billing/stripe/portal');
  },

  async cancelSubscription() {
    return await apiClient.post('/api/billing/stripe/cancel-subscription');
  },

  async reactivateSubscription() {
    return await apiClient.post('/api/billing/stripe/reactivate-subscription');
  },
};

// Root API
export const rootAPI = {
  async getRoot() {
    return await apiClient.get('/');
  }
};

// Export all APIs as a single object for convenience
export const api = {
  auth: authAPI,
  endpoints: endpointsAPI,
  submissions: submissionsAPI,
  billing: billingAPI,
  abuse: abuseAPI,
  formSubmission: formSubmissionAPI,
  root: rootAPI
}; 